import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Heading } from '@chakra-ui/react';
import type { HeadingBlock as HeadingBlockType, ProseMirrorDoc, TypeScaleToken } from '../../editor/types';
import { ADMIN_COLORS } from '../../../../../../generated/tokens/constants';
import { TYPE_SCALE, FONT_PAIRS, LINE_HEIGHT_VALUES, LETTER_SPACING_VALUES, getFontFamilyById } from '../../editor/constants/typographyPresets';
import { resolveColorToken } from '../../editor/constants/colorTokens';
import { loadFontPair, loadFontById } from '../utils/fontLoader';
import { useEffectiveTypography } from '../SectionTypographyContext';
import { useEditorBlockContext } from '../EditorBlockContext';
import { InlineTextEditor } from '../../editor/components/InlineTextEditor';
import { usePreviewDevice } from '../PreviewDeviceContext';

const LEVEL_FONT_SIZE: Record<1 | 2 | 3 | 4, string> = {
  1: '4xl',
  2: '3xl',
  3: '2xl',
  4: 'xl',
};

const LEVEL_TAG: Record<1 | 2 | 3 | 4, 'h1' | 'h2' | 'h3' | 'h4'> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
};

// ---------------------------------------------------------------------------
// Helpers — convert between plain string and ProseMirror doc
// ---------------------------------------------------------------------------
function stringToProseMirror(text: string): ProseMirrorDoc {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
  };
}

function proseMirrorToPlainText(doc: ProseMirrorDoc): string {
  return (
    doc.content
      ?.map((node) => node.content?.map((child) => child.text ?? '').join('') ?? '')
      .join('') ?? ''
  );
}

/** Check if a ProseMirror doc has any marks (bold, italic, etc.) */
function docHasMarks(doc: ProseMirrorDoc): boolean {
  for (const node of doc.content ?? []) {
    for (const child of node.content ?? []) {
      if (child.marks && child.marks.length > 0) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Render rich ProseMirror content inline (marks only, no block structure)
// ---------------------------------------------------------------------------
function renderInlineContent(doc: ProseMirrorDoc): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let keyIdx = 0;

  for (const block of doc.content ?? []) {
    for (const child of block.content ?? []) {
      if (!child.text) continue;
      let node: React.ReactNode = child.text;

      for (const mark of child.marks ?? []) {
        switch (mark.type) {
          case 'bold':
            node = <span key={`m-${keyIdx++}`} style={{ fontWeight: 800 }}>{node}</span>;
            break;
          case 'italic':
            node = <span key={`m-${keyIdx++}`} style={{ fontStyle: 'italic' }}>{node}</span>;
            break;
          case 'underline':
            node = <span key={`m-${keyIdx++}`} style={{ textDecoration: 'underline' }}>{node}</span>;
            break;
          case 'strike':
            node = <span key={`m-${keyIdx++}`} style={{ textDecoration: 'line-through' }}>{node}</span>;
            break;
          case 'link':
            node = <span key={`m-${keyIdx++}`} style={{ textDecoration: 'underline', color: 'var(--chakra-colors-blaze-blaze)' }}>{node}</span>;
            break;
          case 'textStyle': {
            const style: React.CSSProperties = {};
            if (mark.attrs?.color) style.color = mark.attrs.color as string;
            if (mark.attrs?.fontFamily) style.fontFamily = mark.attrs.fontFamily as string;
            node = <span key={`m-${keyIdx++}`} style={style}>{node}</span>;
            break;
          }
        }
      }

      nodes.push(<span key={`t-${keyIdx++}`}>{node}</span>);
    }
  }

  return nodes.length > 0 ? <>{nodes}</> : null;
}

type HeadingBlockProps = HeadingBlockType['props'] & { blockId?: string };

export function HeadingBlock({
  level,
  content,
  rich_content,
  alignment: _alignment, // deprecated — text_align now handled by BlockLayoutWrapper
  color_token,
  typography: blockTypography,
  mobile_size_token,
  blockId,
}: HeadingBlockProps): React.ReactElement {
  const ctx = useEditorBlockContext();
  const isInEditor = ctx?.isEditorCanvas === true && blockId !== undefined;
  const previewDevice = usePreviewDevice();

  // Merge with section-level typography (block overrides section)
  const typography = useEffectiveTypography(blockTypography);

  // Load font pair if specified
  useEffect(() => {
    if (typography?.font_pair) {
      loadFontPair(typography.font_pair);
    }
  }, [typography?.font_pair]);

  // Resolve effective size token based on device context
  const desktopSizeToken = typography?.size_token;
  const effectiveSizeToken: TypeScaleToken | undefined =
    (previewDevice === 'mobile' && mobile_size_token) ? mobile_size_token : desktopSizeToken;

  // Build typography styles
  const typoStyle: React.CSSProperties = {};
  if (effectiveSizeToken && TYPE_SCALE[effectiveSizeToken]) {
    typoStyle.fontSize = `${TYPE_SCALE[effectiveSizeToken].px}px`;
  }
  if (typography?.font_pair) {
    const pair = FONT_PAIRS.find((p) => p.id === typography.font_pair);
    if (pair) typoStyle.fontFamily = pair.heading;
  }
  // Per-block font_family override takes precedence over font_pair
  if (typography?.font_family) {
    const resolvedFamily = getFontFamilyById(typography.font_family);
    if (resolvedFamily) {
      typoStyle.fontFamily = resolvedFamily;
      loadFontById(typography.font_family);
    }
  }
  if (typography?.line_height && LINE_HEIGHT_VALUES[typography.line_height] !== undefined) {
    typoStyle.lineHeight = LINE_HEIGHT_VALUES[typography.line_height];
  }
  if (typography?.letter_spacing && LETTER_SPACING_VALUES[typography.letter_spacing] !== undefined) {
    typoStyle.letterSpacing = LETTER_SPACING_VALUES[typography.letter_spacing];
  }

  // Use rich_content if available, otherwise convert plain string
  const editorContent = useMemo(
    () => rich_content ?? stringToProseMirror(content),
    [rich_content, content],
  );

  // Track latest doc in a ref — save only on blur to avoid re-render cycles
  const pendingDocRef = useRef<ProseMirrorDoc | null>(null);

  const handleChange = useCallback(
    (doc: ProseMirrorDoc) => {
      pendingDocRef.current = doc;
    },
    [],
  );

  const handleBlur = useCallback(() => {
    // Save pending changes on blur
    if (pendingDocRef.current && ctx?.onBlockPropsChange && blockId) {
      const doc = pendingDocRef.current;
      const plainText = proseMirrorToPlainText(doc);
      const updates: Record<string, unknown> = { content: plainText };
      if (docHasMarks(doc)) {
        updates.rich_content = doc;
      } else {
        updates.rich_content = undefined;
      }
      ctx.onBlockPropsChange(ctx.sectionId, blockId, updates);
      pendingDocRef.current = null;
    }

  }, [ctx, blockId]);

  // Production: media query for mobile_size_token when not in editor preview
  const mobileSx = (!previewDevice && mobile_size_token && TYPE_SCALE[mobile_size_token])
    ? { '@media (max-width: 768px)': { fontSize: `${TYPE_SCALE[mobile_size_token].px}px` } }
    : undefined;

  const headingProps = {
    as: LEVEL_TAG[level],
    fontSize: effectiveSizeToken ? undefined : LEVEL_FONT_SIZE[level],
    // text_align is now handled by BlockLayoutWrapper — don't override here
    color: resolveColorToken(color_token) ?? ADMIN_COLORS.textPrimary,
    style: typoStyle,
    sx: mobileSx,
  };

  if (isInEditor) {
    return (
      <Heading gc-testing-id="blockHeadingEditing" {...headingProps} onClick={(e: React.MouseEvent) => { e.stopPropagation(); ctx?.onSelectBlock(ctx.sectionId, blockId!); }}>
        <InlineTextEditor
          content={editorContent}
          onChange={handleChange}
          onBlur={handleBlur}
          singleLine
          placeholder="Enter heading..."
        />
      </Heading>
    );
  }

  // Render with rich content (marks) if available
  const richRendered = rich_content ? renderInlineContent(rich_content) : null;

  return (
    <Heading gc-testing-id="blockHeading" {...headingProps}>
      {richRendered ?? content}
    </Heading>
  );
}
